import { ModelState } from "./model";
import { ModelActions, ModelActionTypes } from "./model.actions";

export const modelFeatureKey = "model";

export const initialModelState: ModelState = {
  summaries: [],
  versions: [],
  loadingModels: false,
  loadingVersions: false,
  activatedModelVersions: [],
  modelReleaseHistory: [],
  error: "",
};

export function reducer(
  state = initialModelState,
  action: ModelActions,
): ModelState {
  switch (action.type) {
    case ModelActionTypes.LoadModels:
      return Object.assign({}, state, { loadingModels: true });

    case ModelActionTypes.LoadModelsSuccess:
      return Object.assign({}, state, {
        summaries: action.payload.data,
        loadingModels: false,
      });

    case ModelActionTypes.AddModel:
      return Object.assign({}, state, { loadingModels: true });

    case ModelActionTypes.AddModelSuccess:
      // Check if it already exists (can happen if the async websocket message already got handled)
      const [existingModel] = state.summaries.filter(
        (m) => m.uuid === action.payload.uuid,
      );
      if (existingModel) {
        return {
          ...state,
          summaries: state.summaries.map((m) =>
            m.uuid === action.payload.uuid ? action.payload : m,
          ),
        };
      } else {
        return {
          ...state,
          summaries: [...state.summaries, action.payload],
          loadingModels: false,
        };
      }

    case ModelActionTypes.MakeModelObsoleteSuccess:
      return {
        ...state,
        summaries: state.summaries.map((s) =>
          s.uuid === action.model.uuid ? { ...s, obsolete: true } : s,
        ),
      };

    case ModelActionTypes.DeleteModelSuccess:
      return {
        ...state,
        summaries: state.summaries.filter((s) => s.uuid !== action.modelId),
        versions: state.versions.filter((v) => v.model_id !== action.modelId),
      };

    case ModelActionTypes.DeleteModelVersionSuccess:
      return {
        ...state,
        activatedModelVersions: state.activatedModelVersions.filter(
          (version) => version.uuid !== action.modelVersionId,
        ),
        versions: state.versions.filter(
          (version) => version.uuid !== action.modelVersionId,
        ),
      };

    case ModelActionTypes.UpdateModelLabelSuccess:
      return {
        ...state,
        summaries: state.summaries.map((s) =>
          s.uuid === action.payload.uuid ? action.payload : s,
        ),
      };

    case ModelActionTypes.LoadAllModelVersions:
      return {
        ...state,
        loadingVersions: true,
      };

    case ModelActionTypes.LoadAllModelVersionsSuccess:
      return {
        ...state,
        versions: action.versions,
        loadingVersions: false,
      };

    case ModelActionTypes.LoadModelVersionsSuccess:
      return {
        ...state,
        activatedModelVersions: action.versions,
      };

    case ModelActionTypes.LoadModelReleaseHistorySuccess:
      return {
        ...state,
        modelReleaseHistory: action.modelReleaseHistory,
      };

    case ModelActionTypes.UpdateModelVersionSuccess:
      const v = state.versions.find((v) => v.uuid === action.modelVersion.uuid);

      if (v) {
        // We only update version data if its newer than what is currently in memory
        const updated = new Date(action.modelVersion.updated).getTime();
        const existing = new Date(v.updated).getTime();
        if (updated <= existing) {
          return state;
        }
      }

      let updatedSummary = state.summaries.find(
        (summary) => summary.released_version === action.modelVersion.uuid,
      );

      if (updatedSummary) {
        // Avoid read only property errors by destructuring the object
        updatedSummary = { ...updatedSummary };
        updatedSummary.released_message = action.modelVersion.message;
      }

      return {
        ...state,
        activatedModelVersions: state.activatedModelVersions.map((v) =>
          v.uuid === action.modelVersion.uuid ? action.modelVersion : v,
        ),
        summaries: state.summaries.map((model) =>
          updatedSummary?.uuid === model.uuid ? updatedSummary : model,
        ),
        versions: state.versions.map((v) =>
          v.uuid === action.modelVersion.uuid ? action.modelVersion : v,
        ),
      };

    case ModelActionTypes.CloneModelVersionSuccess:
      const activatedVersions = [...state.activatedModelVersions];
      const versions = [...state.versions];
      const foundActivatedVersions = activatedVersions.find(
        (version) => version.uuid === action.version.uuid,
      );
      const foundVersion = versions.find(
        (version) => version.uuid === action.version.uuid,
      );
      if (!foundActivatedVersions) {
        activatedVersions.push(action.version);
      }
      if (!foundVersion) {
        versions.push(action.version);
      }

      if (!foundActivatedVersions || !foundVersion) {
        return {
          ...state,
          activatedModelVersions: activatedVersions,
          versions: versions,
        };
      } else {
        return { ...state };
      }

    case ModelActionTypes.ImportLexiconSuccess:
      return {
        ...state,
      };

    case ModelActionTypes.ReleaseModelSuccess:
      return {
        ...state,
        summaries: state.summaries.map((s) =>
          s.uuid === action.model.uuid ? { ...action.model } : s,
        ),
      };

    case ModelActionTypes.ActionFailure:
      return Object.assign({}, state, {
        error: action.error.message,
        loadingModels: false,
      });

    default:
      return state;
  }
}
